// React & MUI
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Typography,
  IconButton,
} from "@mui/material";

// Assets
import arrowback from "../../assets/arrow_back.svg";

// Services
import { getStorageItemLocal } from "../../services/storageServices";

// Config
import { appConfig } from "../../config/appConfig";

// Components
import Spinner from "../spinner/Spinner";

// Styles
import "./PublishIndiaImage.scss";

type AdminEntry = {
  [x: string]: any;
  id: number;
  categoryId?: number;
  imageName: string;
  categoryName: string;
  imageUrls: string | string[];
  isActive: boolean;
  status: string;
};

interface PublishIndiaImageFormDialogProps {
  open: boolean;
  onClose?: () => void;

  fetchData: () => void;
  resetForm: () => void;

  formData: AdminEntry;
  setFormData: React.Dispatch<React.SetStateAction<AdminEntry>>;
  editingIndex: any;
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingIndex: React.Dispatch<React.SetStateAction<any>>;
  dataList: any;
}

const PublishIndiaImageFormDialog: React.FC<
  PublishIndiaImageFormDialogProps
> = ({
  open,
  onClose,
  fetchData,
  resetForm,
  formData,
  setFormData,
  editingIndex,
  setShowForm,
  setEditingIndex,
  dataList,
}) => {
  // File upload states
  const [selectedPdf, setSelectedPdf] = useState<File[]>([]);
  const [fileName, setfileName] = useState("No PDF");

  // UI states
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);

  // Error states
  const [pdferror, setPdfError] = useState<string | null>(null);

  // Constants
  const MAX_FILE_SIZE_MB1 = 15;
  const ALLOWED_TYPE = "application/pdf";

  useEffect(() => {
    setIsActive(formData.isActive);

    let temp: string[] = [];

    if (Array.isArray(formData.imageUrls)) {
      temp = formData.imageUrls.map((img) => img?.trim?.()).filter(Boolean);
    } else if (typeof formData.imageUrls === "string") {
      temp = formData.imageUrls
        .split(",")
        .map((img) => img.trim())
        .filter(Boolean);
    }

    if (temp.length > 0) {
      const extractedFileName = temp[0].split("/").pop()?.trim() ?? "";

      setfileName(extractedFileName);
    } else {
      setfileName(""); // clear filename fallback
    }
  }, [formData]);

  const handleCancel = () => {
    onClose?.();
    setShowForm(false);
    setEditingIndex(null);
    resetForm();
    setShowForm(false);
    setPdfError(null);
    setIsActive(false);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      //   [e.target.name]: e.target.value,
      imageName: e.target.value,
      lastUpdated: new Date().toISOString(),
    });
  };
  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "application/pdf",
  ) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      if (type === "application/pdf") {
        setSelectedPdf(filesArray);
      }
    }
  };

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];

    if (files.length === 0) {
      setPdfError("Please select a PDF file.");
      return;
    }

    const file = files[0]; // Assuming single file upload for PDF

    if (file.type !== ALLOWED_TYPE) {
      setPdfError("Only .pdf files are allowed.");
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB1 * 1024 * 1024) {
      setPdfError("File size must be less than or equal to 10MB.");
      return;
    }

    setPdfError(null);
    handleFileChange(e, "application/pdf"); //file.name
    setfileName(file.name);
  };

  const handleSave = async () => {
    if (!formData.imageName) {
      alert("PDF Name is required!");
      return;
    }
    if (dataList.length >= 3 && editingIndex === null) {
      alert("You cannot add more than three PDFs in India PDF.");
      return;
    }
    const formDataToSend = new FormData();
    formDataToSend.append("id", editingIndex?.toString() ?? "0");
    formDataToSend.append("imageName", formData.imageName);
    formDataToSend.append("isActive", isActive.toString());
    formDataToSend.append("status", "Approved"); //Pending for Approval

    const username = getStorageItemLocal("username") ?? "";
    formDataToSend.append("uploadBy", username);

    if (selectedPdf.length > 0) {
      selectedPdf.forEach((pdf) => {
        formDataToSend.append("imageUrls", pdf);
      });
    } else if (Array.isArray(formData.imageUrls)) {
      for (const url of formData.imageUrls) {
        const cleanUrl = typeof url === "string" ? url.trim() : "";
        if (cleanUrl && cleanUrl !== "null") {
          formDataToSend.append("imageUrls", cleanUrl);
        }
      }
    }
    setLoading(true);
    const url =
      editingIndex !== null
        ? `${appConfig.serviceUrls.saveIndiaPDFEntries}/${formData.id}`
        : `${appConfig.serviceUrls.saveIndiaPDFEntries}/upload`;
    try {
      const response = await fetch(url, {
        method: editingIndex !== null ? "PUT" : "POST",
        body: formDataToSend,
      });
      if (!response.ok) throw new Error("Failed to save data");
      alert("Data saved successfully");
      fetchData();
      resetForm();
      const pdfInput = document.getElementById("pdfUpload") as HTMLInputElement | null;
      if (pdfInput) pdfInput.value = "";
      setSelectedPdf([]);
      setEditingIndex(null);
      setShowForm(false);
    } catch (error) {
      console.error("Error saving data:", error);
    } finally {
      setLoading(false);
    }
    handleCancel();
  };
  const handleIsActive = () => {
    setIsActive(!isActive);
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "900px",
          height: "350px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#F7F7F7",
          padding: "20px 16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <IconButton
            color="primary"
            size="small"
            onClick={handleCancel}
            className="custom-icon-button"
          >
            <img src={arrowback} alt="visible" className="custom-icon" />
          </IconButton>
          <Typography className="custom-icon-text">
            {editingIndex !== null
              ? "Edit India Level PDF"
              : "Add India Level PDF"}
          </Typography>
        </Box>

        <Box></Box>
      </Box>

      {loading ? (
        <>
          {" "}
          <Spinner></Spinner>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            background: "#FFF",
            padding: "50px 16px",
          }}
        >
          <Box>
            <Typography className="text-style">PDF Name</Typography>
            <Box className="pdf-name-wrapper">
              <TextField
                placeholder="Enter PDF Name"
                size="small"
                name="imageName"
                value={formData.imageName}
                onChange={handleChange}
                variant="standard"
                fullWidth
                InputProps={{ disableUnderline: true }}
              />
            </Box>
          </Box>
          <Box>
            <Typography className="text-style">Add PDF File</Typography>
            <Box className="pdf-upload-row">
              <Box className="pdf-name-wrapper">
                <TextField
                  placeholder="Select PDF"
                  size="small"
                  name="imageName"
                  value={fileName}
                  onChange={handleChange}
                  variant="standard"
                  fullWidth
                  disabled={true}
                  InputProps={{ disableUnderline: true }}
                />
              </Box>

              <Box>
                <Button component="label" className="choose-pdf-button">
                  <Typography className="pdf-button-text ">Browse</Typography>
                  <input
                    id="pdfUpload"
                    type="file"
                    accept="application/pdf"
                    hidden
                    onChange={handlePdfUpload}
                  />
                </Button>
                {pdferror && <Alert severity="error">{pdferror}</Alert>}
              </Box>
            </Box>
          </Box>
          <Box>
            <Typography className="text-style">Active/Disable</Typography>

            <Box
              onClick={handleIsActive}
              sx={{ padding: "15px" }}
              className="status-wrapper"
            >
              <Box
                className={`status-toggle1 ${isActive ? "active" : "Disable"}`}
              >
                <Box className="status-circle" />
              </Box>

              <Typography
                sx={{ width: "30px" }}
                className="active-txt"
                component="span"
              >
                {isActive ? "Active" : "Disable"}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <DialogActions>
        <Button
          onClick={handleCancel}
          className="cancel-button"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          disabled={pdferror != null}
          onClick={handleSave}
          className="submit-button"
        >
          {editingIndex !== null ? "Update" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishIndiaImageFormDialog;
